export interface NetworkInformationLike {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
}

export interface NavigationTimingLike {
  name?: string;
  type?: string;
  fetchStart?: number;
  redirectStart?: number;
  redirectEnd?: number;
  redirectCount?: number;
  workerStart?: number;
  finalResponseHeadersStart?: number;
  nextHopProtocol?: string;
  deliveryType?: string;
  decodedBodySize?: number;
  startTime?: number;
  activationStart?: number;
  requestStart?: number;
  responseStart?: number;
  responseEnd?: number;
  domainLookupStart?: number;
  domainLookupEnd?: number;
  connectStart?: number;
  secureConnectionStart?: number;
  connectEnd?: number;
  transferSize?: number;
  encodedBodySize?: number;
}

export interface NetworkSnapshotSource {
  navigator?: {
    onLine?: boolean;
    connection?: NetworkInformationLike;
  };
  performance?: {
    navigationEntry?: NavigationTimingLike | null;
  };
}

export interface NetworkSnapshot {
  net_online: boolean;
  net_effective_type: string;
  net_rtt: number;
  net_downlink: number;
  net_save_data: boolean;
  /** 历史口径：requestStart → responseStart，保留以兼容旧数据。 */
  nav_ttfb: number;
  /** 标准导航 TTFB：startTime/activationStart → responseStart。 */
  nav_standard_ttfb: number;
  nav_dns: number;
  nav_tcp: number;
  nav_tls: number;
  nav_download: number;
  xn_is_weak_network: boolean;
}

export const DEFAULT_NETWORK_SNAPSHOT: NetworkSnapshot = {
  net_online: false,
  net_effective_type: '',
  net_rtt: -1,
  net_downlink: -1,
  net_save_data: false,
  nav_ttfb: -1,
  nav_standard_ttfb: -1,
  nav_dns: -1,
  nav_tcp: -1,
  nav_tls: -1,
  nav_download: -1,
  xn_is_weak_network: false,
};

function safeDuration(end?: number, start?: number): number {
  if (typeof end !== 'number' || typeof start !== 'number') return -1;
  const duration = end - start;
  return duration >= 0 ? Math.round(duration) : -1;
}

function getNavigationTtfb(entry: NavigationTimingLike | null | undefined): number {
  const navigationStart =
    typeof entry?.activationStart === 'number' && entry.activationStart > 0
      ? entry.activationStart
      : entry?.startTime;

  if (typeof entry?.responseStart !== 'number' || typeof navigationStart !== 'number') {
    return -1;
  }

  return Math.round(Math.max(entry.responseStart - navigationStart, 0));
}

function roundDownlink(value: number): number {
  return Math.round(value * 100) / 100;
}

function estimateDownlink(entry: NavigationTimingLike | null | undefined): number {
  if (!entry) return -1;

  const bytes =
    typeof entry.transferSize === 'number' && entry.transferSize > 0
      ? entry.transferSize
      : typeof entry.encodedBodySize === 'number' && entry.encodedBodySize > 0
        ? entry.encodedBodySize
        : 0;
  const downloadDuration = safeDuration(entry.responseEnd, entry.responseStart);

  if (bytes > 0 && downloadDuration > 0) {
    return roundDownlink((bytes * 8) / (downloadDuration * 1000));
  }

  return -1;
}

export type NetworkDownlinkSource = 'response_estimate' | 'connection_api' | 'unavailable';

/** Metadata for the same source used by the legacy snapshot, including its rounding fallback. */
export function getNetworkDownlinkSource(source: NetworkSnapshotSource): NetworkDownlinkSource {
  if (estimateDownlink(source.performance?.navigationEntry) > 0) return 'response_estimate';
  const downlink = source.navigator?.connection?.downlink;
  return typeof downlink === 'number' && downlink > 0 ? 'connection_api' : 'unavailable';
}

export function isWeakNetwork(
  snapshot: Pick<NetworkSnapshot, 'net_downlink' | 'net_effective_type' | 'net_rtt'>,
): boolean {
  return (
    (snapshot.net_downlink > 0 && snapshot.net_downlink < 1.5) ||
    ['slow-2g', '2g', '3g'].includes(snapshot.net_effective_type) ||
    (snapshot.net_rtt > 0 && snapshot.net_rtt > 500)
  );
}

export function getBrowserNetworkSnapshotSource(): NetworkSnapshotSource {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const perf = typeof performance !== 'undefined' ? performance : undefined;
  const connection = nav
    ? (nav as Navigator & { connection?: NetworkInformationLike }).connection
    : undefined;
  const navigationEntry = perf?.getEntriesByType
    ? (perf.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
    : undefined;

  return {
    navigator: nav
      ? {
          onLine: nav.onLine,
          connection,
        }
      : undefined,
    performance: {
      navigationEntry,
    },
  };
}

export function getNetworkSnapshot(
  source: NetworkSnapshotSource = getBrowserNetworkSnapshotSource(),
): NetworkSnapshot {
  const connection = source.navigator?.connection;
  const navigationEntry = source.performance?.navigationEntry;
  const navigationDownlink = estimateDownlink(navigationEntry);
  const fallbackDownlink =
    typeof connection?.downlink === 'number' && connection.downlink > 0 ? connection.downlink : -1;

  const snapshot: NetworkSnapshot = {
    net_online: typeof source.navigator?.onLine === 'boolean' ? source.navigator.onLine : false,
    net_effective_type: connection?.effectiveType ?? '',
    net_rtt: typeof connection?.rtt === 'number' ? Math.round(connection.rtt) : -1,
    net_downlink: navigationDownlink > 0 ? navigationDownlink : fallbackDownlink,
    net_save_data: !!connection?.saveData,
    nav_ttfb: safeDuration(navigationEntry?.responseStart, navigationEntry?.requestStart),
    nav_standard_ttfb: getNavigationTtfb(navigationEntry),
    nav_dns: safeDuration(navigationEntry?.domainLookupEnd, navigationEntry?.domainLookupStart),
    nav_tcp: safeDuration(navigationEntry?.connectEnd, navigationEntry?.connectStart),
    nav_tls:
      typeof navigationEntry?.secureConnectionStart === 'number' &&
      navigationEntry.secureConnectionStart > 0
        ? safeDuration(navigationEntry.connectEnd, navigationEntry.secureConnectionStart)
        : -1,
    nav_download: safeDuration(navigationEntry?.responseEnd, navigationEntry?.responseStart),
    xn_is_weak_network: false,
  };

  snapshot.xn_is_weak_network = isWeakNetwork(snapshot);
  return snapshot;
}
